import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import config from '../config/env';
import { 
  ApiResponse, 
  AuthResponse, 
  LoginForm, 
  Reservation, 
  ReservationFilters,
  CreateReservationData,
  UpdateReservationStatusData,
  Restaurant,
  RestaurantStats,
  Table,
  AvailabilityResponse,
  PaginationParams,
  User,
  AvailabilityClosure,
  CreateClosureData,
  UpdateClosureData,
  ClosureFilters
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Use environment configuration
    this.baseURL = config.apiUrl;
    
    // Debug logging to help identify configuration issues
    console.log('🔌 ApiService initialized with baseURL:', this.baseURL);
    console.log('🔌 REACT_APP_API_URL from env:', process.env.REACT_APP_API_URL);
    
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - dispatch custom event for AuthContext to handle
          const event = new CustomEvent('auth:session-expired');
          window.dispatchEvent(event);
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginForm): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>('/auth/login', credentials);
    return response.data.data;
  }

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    try {
      const response = await this.api.post<ApiResponse<AuthResponse>>('/auth/refresh', {
        refresh_token: refreshToken
      });
      return response.data.data;
    } catch (error: any) {
      // If refresh endpoint doesn't exist or fails, try to get profile with current token
      if (error.response?.status === 404 || error.response?.status === 401) {
        const profileResponse = await this.api.get<ApiResponse<User>>('/auth/profile');
        // If profile works, return current token info
        return {
          token: localStorage.getItem('auth_token') || '',
          refresh_token: refreshToken,
          user: profileResponse.data.data,
          expires_in: 3600 // Default 1 hour
        };
      }
      throw error;
    }
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>('/auth/profile');
    return response.data.data;
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.api.put<ApiResponse<User>>('/auth/profile', data);
    return response.data.data;
  }

  async changePassword(data: { current_password: string; new_password: string; confirm_password: string }): Promise<void> {
    await this.api.put('/auth/password', data);
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  // Reservation endpoints
  async getReservations(filters?: ReservationFilters & PaginationParams): Promise<ApiResponse<Reservation[]>> {
    // Clean up filters to remove empty values
    const cleanFilters = filters ? Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined && value !== null)
    ) : {};
    
    const response = await this.api.get<ApiResponse<Reservation[]>>('/reservas', { params: cleanFilters });
    return response.data;
  }

  async getTodayReservations(): Promise<Reservation[]> {
    const response = await this.api.get<ApiResponse<Reservation[]>>('/reservas/hoje');
    return response.data.data;
  }

  async getReservationsByRestaurantAndDate(restaurantId: number, date: string): Promise<Reservation[]> {
    const response = await this.api.get<ApiResponse<{
      restaurante_id: number;
      date: string;
      total: number;
      by_status: Record<string, Reservation[]>;
      reservations: Reservation[];
    }>>(`/reservas/restaurante/${restaurantId}/data`, {
      params: { data: date }
    });
    return response.data.data.reservations;
  }

  async getReservationById(id: number): Promise<Reservation> {
    const response = await this.api.get<ApiResponse<Reservation>>(`/reservas/${id}`);
    return response.data.data;
  }

  async createReservation(data: CreateReservationData): Promise<Reservation> {
    const response = await this.api.post<ApiResponse<Reservation>>('/public/reservas', data);
    return response.data.data;
  }

  async createReservationAsManager(data: CreateReservationData): Promise<Reservation> {
    // Manager endpoint - requires authentication
    // Benefits:
    // - Auto-confirmed (status: 'confirmada')
    // - No payment required (payment_status: 'not_required')
    // - Bypasses minimum advance time restrictions
    // - Sends confirmation email to customer
    const response = await this.api.post<ApiResponse<Reservation>>('/reservas', data);
    return response.data.data;
  }

  async updateReservationStatus(id: number, data: UpdateReservationStatusData): Promise<Reservation> {
    const response = await this.api.patch<ApiResponse<Reservation>>(`/reservas/${id}/status`, data);
    return response.data.data;
  }

  async cancelReservation(id: number): Promise<Reservation> {
    const response = await this.api.patch<ApiResponse<Reservation>>(`/reservas/${id}/cancelar`);
    return response.data.data;
  }

  async refundReservation(id: number): Promise<Reservation> {
    const response = await this.api.post<ApiResponse<Reservation>>(`/reservas/${id}/refund`);
    return response.data.data;
  }

  // Restaurant endpoints
  async getRestaurants(params?: PaginationParams): Promise<ApiResponse<Restaurant[]>> {
    const response = await this.api.get<ApiResponse<Restaurant[]>>('/restaurantes', { params });
    return response.data;
  }

  async getRestaurantById(id: number): Promise<Restaurant> {
    const response = await this.api.get<ApiResponse<Restaurant>>(`/restaurantes/${id}`);
    return response.data.data;
  }

  async updateRestaurant(id: number, data: Partial<Restaurant>): Promise<Restaurant> {
    const response = await this.api.patch<ApiResponse<Restaurant>>(`/restaurantes/${id}`, data);
    return response.data.data;
  }

  async getRestaurantStats(id: number, period?: number): Promise<RestaurantStats> {
    const response = await this.api.get<ApiResponse<RestaurantStats>>(
      `/restaurantes/${id}/estatisticas`,
      { params: { period } }
    );
    return response.data.data;
  }

  async getPublicConfig(slug: string): Promise<Restaurant> {
    const response = await this.api.get<ApiResponse<Restaurant>>(`/public/restaurantes/${slug}/config`);
    return response.data.data;
  }

  async checkAvailability(slug: string, date: string): Promise<AvailabilityResponse> {
    const response = await this.api.get<ApiResponse<AvailabilityResponse>>(
      `/public/restaurantes/${slug}/disponibilidade`,
      { params: { date } }
    );
    return response.data.data;
  }

  // Table endpoints
  async getTables(params?: PaginationParams): Promise<ApiResponse<Table[]>> {
    const response = await this.api.get<ApiResponse<Table[]>>('/mesas', { params });
    return response.data;
  }

  async getTableById(id: number): Promise<Table> {
    const response = await this.api.get<ApiResponse<Table>>(`/mesas/${id}`);
    return response.data.data;
  }

  async createTable(data: Omit<Table, 'id' | 'criado_em' | 'atualizado_em'>): Promise<Table> {
    const response = await this.api.post<ApiResponse<Table>>('/mesas', data);
    return response.data.data;
  }

  async updateTable(id: number, data: Partial<Table>): Promise<Table> {
    const response = await this.api.patch<ApiResponse<Table>>(`/mesas/${id}`, data);
    return response.data.data;
  }

  async deleteTable(id: number): Promise<void> {
    await this.api.delete(`/mesas/${id}`);
  }

  async getAvailableTables(restauranteId: number, dataHora: string, numeroPessoas: number): Promise<Table[]> {
    const response = await this.api.get<ApiResponse<Table[]>>(
      `/mesas/restaurante/${restauranteId}/disponiveis`,
      { 
        params: { 
          data_hora: dataHora, 
          numero_pessoas: numeroPessoas 
        } 
      }
    );
    return response.data.data;
  }

  // Availability Closure endpoints
  async getClosures(filters?: ClosureFilters & PaginationParams): Promise<ApiResponse<AvailabilityClosure[]>> {
    const cleanFilters = filters ? Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined && value !== null)
    ) : {};
    
    const response = await this.api.get<ApiResponse<AvailabilityClosure[]>>('/fechamentos-disponibilidade', { params: cleanFilters });
    return response.data;
  }

  async getClosureById(id: number): Promise<ApiResponse<AvailabilityClosure>> {
    const response = await this.api.get<ApiResponse<AvailabilityClosure>>(`/fechamentos-disponibilidade/${id}`);
    return response.data;
  }

  async createClosure(data: CreateClosureData): Promise<ApiResponse<AvailabilityClosure>> {
    const response = await this.api.post<ApiResponse<AvailabilityClosure>>('/fechamentos-disponibilidade', data);
    return response.data;
  }

  async updateClosure(id: number, data: UpdateClosureData): Promise<ApiResponse<AvailabilityClosure>> {
    const response = await this.api.patch<ApiResponse<AvailabilityClosure>>(`/fechamentos-disponibilidade/${id}`, data);
    return response.data;
  }

  async toggleClosure(id: number, ativo: boolean): Promise<ApiResponse<AvailabilityClosure>> {
    const response = await this.api.patch<ApiResponse<AvailabilityClosure>>(`/fechamentos-disponibilidade/${id}/toggle`, { ativo });
    return response.data;
  }

  async deleteClosure(id: number): Promise<ApiResponse<void>> {
    const response = await this.api.delete<ApiResponse<void>>(`/fechamentos-disponibilidade/${id}`);
    return response.data;
  }

  async checkClosureForDateTime(restauranteId: number, dataHora: string): Promise<ApiResponse<{ closed: boolean; reason?: string }>> {
    const response = await this.api.get<ApiResponse<{ closed: boolean; reason?: string }>>(
      `/fechamentos-disponibilidade/restaurante/${restauranteId}/verificar`,
      { params: { data_hora: dataHora } }
    );
    return response.data;
  }

  async getClosuresForPeriod(restauranteId: number, dataInicio: string, dataFim: string): Promise<ApiResponse<AvailabilityClosure[]>> {
    const response = await this.api.get<ApiResponse<AvailabilityClosure[]>>(
      `/fechamentos-disponibilidade/restaurante/${restauranteId}/periodo`,
      { params: { data_inicio: dataInicio, data_fim: dataFim } }
    );
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ success: boolean; message: string; timestamp: string; version: string }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Push Notification endpoints
  async subscribeToPushNotifications(subscriptionData: {
    endpoint: string;
    keys: {
      p256dh: string;
      auth: string;
    };
    user_id?: number;
    restaurant_id?: number;
  }): Promise<void> {
    await this.api.post('/push-subscriptions', subscriptionData);
  }

  async unsubscribeFromPushNotifications(endpoint: string): Promise<void> {
    await this.api.delete('/push-subscriptions', { data: { endpoint } });
  }

  async getVapidPublicKey(): Promise<string> {
    const response = await this.api.get<ApiResponse<{ public_key: string }>>('/push-subscriptions/vapid-public-key');
    return response.data.data.public_key;
  }
}

export const apiService = new ApiService();
export default apiService;
